import { DashboardCard, Card, CardTitle, CardDescription } from "@tellescope/ui";
import { useDashboardTicketsData } from "../hooks/useDashboardTicketsData";
import { useTranslation } from "react-i18next";
import { Link } from "../../../components";
import { PermissionWrapper } from "../../../permissions";

export interface DashboardTicketsContainerProps {
  className?: string;
  ticketLimit?: number;
}

export function DashboardTicketsContainer({ className = "", ticketLimit = 10 }: DashboardTicketsContainerProps) {
  const { t } = useTranslation("dashboard");

  // Use real data hook
  const { ticketCards, isLoading, error } = useDashboardTicketsData(ticketLimit);

  return (
    <PermissionWrapper model="tickets" action="read">
      <Card className={`px-6 ${className}`}>
        {/* Header */}
        <div className="grid flex-1 gap-1">
          <CardTitle>{t("Ticketing activity")}</CardTitle>
          <CardDescription>{t("Track and manage your open tickets")}</CardDescription>
        </div>

        {/* Scrolling list of tickets */}
        <div className="h-[600px] overflow-y-auto space-y-4">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-sm text-muted-foreground">{t("Loading tickets...")}</div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-sm text-destructive">
                {t("Error loading tickets: {{error}}", { error: error.message })}
              </div>
            </div>
          ) : ticketCards.length === 0 ? (
            <div className="flex items-center justify-center h-32">
              <div className="text-sm text-muted-foreground">{t("No tickets found")}</div>
            </div>
          ) : (
            ticketCards.map((card, index) => (
              <Link
                key={index}
                to={`/tickets/details/${card.ticketId}`}
                className="block"
                data-testid="dashboard-ticket-link"
              >
                <DashboardCard
                  title={card.title}
                  description={card.description}
                  dueDate={card.dueDate}
                  statusLabel={card.statusLabel}
                  status={card.status}
                  priority={card.priority}
                  assignees={card.assignees}
                  timestamp={card.timestamp}
                  customStatusStyles={card.customStatusStyles}
                  customPriorityStyles={card.customPriorityStyles}
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                />
              </Link>
            ))
          )}
        </div>
      </Card>
    </PermissionWrapper>
  );
}
